import mainApiClient from "../../helpers/services/main-api-client";

export interface PlatformAutomation {
  id: number;
  name: string;
  description?: string;
  status: string;
  draftDefinition?: Record<string, unknown>;
  publishedVersionId?: number;
  stats?: Record<string, number>;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlatformAutomationEnrollmentRow {
  id: number;
  userId: number;
  userEmail: string | null;
  userName: string | null;
  status: string;
  isTest: boolean;
  triggeredAt: string;
  completedAt?: string | null;
  stoppedAt?: string | null;
  stopReason?: string | null;
  stopScope?: string | null;
  automationVersionId: number;
  versionNumber: number | null;
  currentStepId?: string | null;
  currentStepOrder?: number | null;
  currentStepLabel?: string | null;
  progress: { emailsSent: number; emailsTotal: number };
  nextScheduleAt?: string | null;
  nextStepLabel?: string | null;
  triggerSource: string;
  recipientEmail?: string | null;
}

export interface PlatformAutomationActivityLogRow {
  id: number;
  event: string;
  automationId?: number;
  enrollmentId?: number | null;
  stepId?: string | null;
  userId?: number | null;
  userEmail?: string | null;
  channel?: string | null;
  provider?: string | null;
  templateId?: string | null;
  templateName?: string | null;
  emailSubject?: string | null;
  stepLabel?: string | null;
  providerMessageId?: string | null;
  attempt?: number | null;
  detail?: string | null;
  createdAt?: string;
  enrollmentLabel?: string | null;
}

export interface PlatformAutomationUserEmailRow {
  id: number;
  automationId: number;
  automationName: string;
  enrollmentId?: number | null;
  subject?: string | null;
  templateName?: string | null;
  contentPreview?: string | null;
  providerMessageId?: string | null;
  sentAt: string;
}

export interface PlatformMessageTemplate {
  id: number;
  name: string;
  description?: string;
  locale: string;
  channel: string;
  status: string;
  publishedVersionId?: number;
  createdAt?: string;
  updatedAt?: string;
  draftDefinition?: {
    subject?: string;
    htmlBody?: string;
    textBody?: string;
    variables?: string[];
  };
}

const client = () => mainApiClient();

export const platformAutomationsApi = {
  list: async () => {
    const { data } = await client().get("/admin/platform-automations");
    return data.payload.automations as PlatformAutomation[];
  },
  get: async (id: number) => {
    const { data } = await client().get(`/admin/platform-automations/${id}`);
    return data.payload.automation as PlatformAutomation & { versions: unknown[] };
  },
  create: async (body: Partial<PlatformAutomation>) => {
    const { data } = await client().post("/admin/platform-automations", body);
    return data.payload.automation;
  },
  updateDraft: async (id: number, body: Partial<PlatformAutomation>) => {
    const { data } = await client().put(
      `/admin/platform-automations/${id}/draft`,
      body
    );
    return data.payload.automation;
  },
  publish: async (id: number) => {
    const { data } = await client().post(
      `/admin/platform-automations/${id}/publish`
    );
    return data.payload.automation;
  },
  pause: async (id: number) => {
    const { data } = await client().post(
      `/admin/platform-automations/${id}/pause`
    );
    return data.payload.automation;
  },
  duplicate: async (id: number) => {
    const { data } = await client().post(
      `/admin/platform-automations/${id}/duplicate`
    );
    return data.payload.automation;
  },
  listTemplates: async () => {
    const { data } = await client().get("/admin/platform-message-templates");
    return data.payload.templates as PlatformMessageTemplate[];
  },
  getTemplate: async (id: number) => {
    const { data } = await client().get(
      `/admin/platform-message-templates/${id}`
    );
    return data.payload.template as PlatformMessageTemplate & {
      versions?: unknown[];
    };
  },
  createTemplate: async (body: Partial<PlatformMessageTemplate>) => {
    const { data } = await client().post(
      "/admin/platform-message-templates",
      body
    );
    return data.payload.template;
  },
  updateTemplateDraft: async (
    id: number,
    body: Partial<PlatformMessageTemplate>
  ) => {
    const { data } = await client().put(
      `/admin/platform-message-templates/${id}/draft`,
      body
    );
    return data.payload.template;
  },
  publishTemplate: async (id: number) => {
    const { data } = await client().post(
      `/admin/platform-message-templates/${id}/publish`
    );
    return data.payload.template;
  },
  listEnrollments: async (automationId: number) => {
    const { data } = await client().get(
      `/admin/platform-automations/${automationId}/enrollments`
    );
    return data.payload.enrollments as PlatformAutomationEnrollmentRow[];
  },
  listLogs: async (automationId: number, enrollmentId?: number) => {
    const { data } = await client().get(
      `/admin/platform-automations/${automationId}/logs`,
      { params: enrollmentId ? { enrollmentId } : undefined }
    );
    return data.payload.logs as PlatformAutomationActivityLogRow[];
  },
  listUserEmails: async (userId: string | number) => {
    const { data } = await client().get(
      `/admin/platform-automations/users/${userId}/emails`
    );
    return data.payload.emails as PlatformAutomationUserEmailRow[];
  },
  stopEnrollment: async (enrollmentId: number) => {
    await client().post(
      `/admin/platform-automations/enrollments/${enrollmentId}/stop`
    );
  },
  testEmail: async (body: {
    step: Record<string, unknown>;
    snapshot: Record<string, unknown>;
    targetEmail?: string;
  }) => {
    await client().post("/admin/platform-automations/test-email", body);
  },
  testFlow: async (
    automationId: number,
    body: {
      snapshot: Record<string, unknown>;
      targetEmail?: string;
    }
  ) => {
    const { data } = await client().post(
      `/admin/platform-automations/${automationId}/test-flow`,
      body
    );
    return data.payload.result as {
      enrollmentId: number;
      firstStepId: string | null;
      firstScheduleAt: string | null;
      recipient: string;
    };
  },
};
